import React, { useEffect, useMemo, useState } from 'react';
import { BaseTabs } from '@/components';
import { Layout } from '@/layouts/Layout';
import { useActiveTab, useAppFilters } from '@/hooks/shared';
import { dispatch, RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import { HEALTH_STATUS, REGULAR_DATE_FORMAT, SENSORS_DATA_TYPE } from '@/const';
import { format } from 'date-fns';
import { AssetStudio } from '@/components/pages/AssetsStudioPage/AssetStudio';
import { DatePicker } from '@/components/uiKit/DatePicker/DatePicker';
import { ASSET_STUDIO, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { useDiagnosticChartLayout } from '@/hooks/AssetsStudioPage/shared/useDiagnosticChartLayout';
import { setAssetsIds, setComponentIds } from '@/store/slices/assetsPreviewSlice/alarmsSlice';
import { useParams } from 'react-router-dom';
import { setAlarmsDateRange, setDateRange } from '@/store/slices/assetStudioSlice';
import { AlarmsActions } from '@/components/pages/AssetsStudioPage/shared/AlarmsActions';
import { useTranslation } from 'react-i18next';
import { useFetchDiagnostics } from '@/hooks/AssetsStudioPage/shared/useFetchDiagnostics';
import { sortComponentsByHealthStatusAndName, componentHasCamera } from '@/components/pages/AssetsStudioPage/SidePane/ComponentSelector/util';
import { BaseButton } from '@/components/uiKit/Button';
import { useFetchFaults } from '@/hooks';
import { useItemsDrawer } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/hooks/useItemsDrawer';

//@TODO: move to util
const colors = {
  healthy: 'bg-healthy-light',
  alarm: 'bg-alarm-light',
  monitor: 'bg-monitor-light',
  critical: 'bg-critical-light',
  na: 'bg-notMonitored-light',
};

export const AssetStudioPage = () => {
  const { t } = useTranslation();
  const { assetStudio, selectedComponent, setFilters } = useAppFilters();
  const { id: selectedAsset } = useParams();
  const { isSensorData, isDiagnostics, isAlarms, isMaintenance, isVisualAI } = useActiveTab();
  const { isOpen, setIsOpen, showMaint, showAlarms, handleToggle } = useDiagnosticChartLayout();
  const { isMobileAsset } = useActiveAssetsType();
  const { selectedDiagnostic, selectedComponentObj, dateRange, alarmsDateRange } = useSelector(
    (state: RootState) => state.assetsStudio
  );
  const isAllComponentsSelected =
    selectedComponent === ASSET_STUDIO.ALL_COMPONENTS || selectedComponent === ASSET_STUDIO.UNASSIGNED;

  // Memoize dates to prevent infinite re-renders (new Date creates new object every render)
  const memoizedFrom = useMemo(() => new Date(dateRange.from), [dateRange.from]);
  const memoizedTo = useMemo(() => new Date(dateRange.to), [dateRange.to]);

  // Get diagnostics data to access components list
  const { diagnostics } = useFetchDiagnostics(memoizedFrom, memoizedTo);
  const { faultsRaw: faults } = useFetchFaults();

  // Memoize the faults array to prevent creating new references when undefined
  const stableFaults = useMemo(() => faults ?? [], [faults]);
  const { openDrawer } = useItemsDrawer(stableFaults);

  // Check if View Faults button should be disabled
  // Disabled when health_status is healthy/na AND there are no faults (fault_type is null)
  const componentHealthStatus = selectedComponentObj?.health_status;
  const isHealthyOrNA =
    componentHealthStatus === HEALTH_STATUS.HEALTHY || componentHealthStatus === HEALTH_STATUS.NOT_AVAILABLE;
  const hasFaults = faults && faults.length > 0;
  const isViewFaultsDisabled = isHealthyOrNA && !hasFaults;

  // Get the first Data Studio component (no camera)
  const firstDataStudioComponent = useMemo(() => {
    const components = diagnostics?.[diagnostics?.length - 1]?.[1]?.assets?.[0]?.components;
    const sortedComponents = sortComponentsByHealthStatusAndName(components);
    return sortedComponents?.find((comp: any) => !componentHasCamera(comp))?.component_id;
  }, [diagnostics]);

  // Get the first Visual AI component (has camera)
  const firstVisualAIComponent = useMemo(() => {
    const components = diagnostics?.[diagnostics?.length - 1]?.[1]?.assets?.[0]?.components;
    const sortedComponents = sortComponentsByHealthStatusAndName(components);
    return sortedComponents?.find((comp: any) => componentHasCamera(comp))?.component_id;
  }, [diagnostics]);

  // Handle tab click - when clicking SENSOR_DATA or VISUAL_AI tab while Asset Overview is selected,
  // first select the appropriate component, then navigate to the tab
  const handleTabClick = (tabId: string) => {
    if (tabId === ASSET_STUDIO.SENSOR_DATA && isAllComponentsSelected && firstDataStudioComponent) {
      // Set both the first Data Studio component AND navigate to sensor data simultaneously
      setFilters({
        [FILTER_PARAMS.SELECTED_COMPONENT]: firstDataStudioComponent,
        [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
      });
    } else if (tabId === ASSET_STUDIO.VISUAL_AI && isAllComponentsSelected && firstVisualAIComponent) {
      // Set both the first Visual AI component AND navigate to visual AI simultaneously
      setFilters({
        [FILTER_PARAMS.SELECTED_COMPONENT]: firstVisualAIComponent,
        [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.VISUAL_AI,
      });
    }
  };

  // Auto-select appropriate component when diagnostics load if user is on Sensor Data or Visual AI tab with Asset Overview
  useEffect(() => {
    if (
      assetStudio === ASSET_STUDIO.SENSOR_DATA &&
      isAllComponentsSelected &&
      firstDataStudioComponent &&
      selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
    ) {
      setFilters({
        [FILTER_PARAMS.SELECTED_COMPONENT]: firstDataStudioComponent,
      });
    } else if (
      assetStudio === ASSET_STUDIO.VISUAL_AI &&
      isAllComponentsSelected &&
      firstVisualAIComponent &&
      selectedComponent === ASSET_STUDIO.ALL_COMPONENTS
    ) {
      setFilters({
        [FILTER_PARAMS.SELECTED_COMPONENT]: firstVisualAIComponent,
      });
    }
  }, [firstDataStudioComponent, firstVisualAIComponent, assetStudio, isAllComponentsSelected, selectedComponent, setFilters]);

  // Check if currently selected component has camera capability
  // IMPORTANT: Always check against the LATEST diagnostics data, not the selected diagnostic date
  // This ensures tabs are enabled based on component capabilities, not just the selected date's data
  const selectedComponentHasCamera = useMemo(() => {
    if (isAllComponentsSelected || !selectedComponent || !diagnostics) return false;

    // Always use the latest diagnostics data (last entry) to determine camera capability
    const components = diagnostics?.[diagnostics?.length - 1]?.[1]?.assets?.[0]?.components;
    const comp = components?.find((c: any) => c.component_id === selectedComponent);

    return comp ? componentHasCamera(comp) : false;
  }, [selectedComponent, diagnostics, isAllComponentsSelected]);

  const assetStudioTabs = useMemo(() => {
    return [
      { id: ASSET_STUDIO.DIAGNOSTICS, title: t('page.studio.tabs.diagnostics.title') },
      {
        id: ASSET_STUDIO.SENSOR_DATA,
        title: t('page.studio.tabs.sensorData.title'),
        // When Asset Overview selected: enable only if there's at least one Data Studio component (no camera)
        // When specific component selected: disable if component has a camera
        disabled: isAllComponentsSelected
          ? !firstDataStudioComponent
          : selectedComponentHasCamera,
        tooltip: selectedComponentHasCamera ? t('page.studio.tabs.sensorData.tooltip') : undefined,
      },
      {
        id: ASSET_STUDIO.VISUAL_AI,
        title: t('page.studio.tabs.visualAI.title'),
        // When Asset Overview selected: enable only if there's at least one Visual AI component (has camera)
        // When specific component selected: disable if component doesn't have a camera
        disabled: isAllComponentsSelected
          ? !firstVisualAIComponent
          : !selectedComponentHasCamera,
        tooltip: !selectedComponentHasCamera ? t('assetStudio.tabs.visualAI.tooltip') : undefined,
      },
      { id: ASSET_STUDIO.MAINTENANCE_DATA, title: t('page.studio.tabs.maintenance.title') },
      { id: ASSET_STUDIO.ALARMS, title: t('assetStudio.tabs.alarms.title'), disabled: !isMobileAsset },
    ];
  }, [selectedComponentHasCamera, isAllComponentsSelected, firstDataStudioComponent, firstVisualAIComponent, isMobileAsset, t]);

  const [activeSensorsDataType, setActiveSensorsDataType] = useState<string>(SENSORS_DATA_TYPE.PI);
  const currentDiagnosticHealthStatus = isAllComponentsSelected
    ? colors[selectedDiagnostic?.[1]?.assets?.[0]?.health_status as 'critical']
    : colors[
        selectedDiagnostic?.[1]?.assets?.[0]?.components.find((el: any) => el.component_id === selectedComponent)
          ?.health_status as 'critical'
      ];

  const MemoizedDatePicker = useMemo(() => {
    const limitUnit = isAlarms ? 'days' : 'months';
    const limitValue = isAlarms ? 7 : 2;
    const onDateUpdate = (range: any) => {
      // Convert Date objects to ISO strings to avoid Redux serialization warnings
      const serializedRange = {
        from: range.from instanceof Date ? range.from.toISOString() : range.from,
        to: range.to instanceof Date ? range.to.toISOString() : range.to,
      };
      isAlarms ? dispatch(setAlarmsDateRange(serializedRange)) : dispatch(setDateRange(serializedRange));
    };
    const { from, to } = isAlarms ? alarmsDateRange : dateRange;
    return (
      <DatePicker
        limitUnit={limitUnit}
        limitValue={limitValue}
        onDateUpdate={onDateUpdate}
        from={new Date(from)}
        to={new Date(to)}
      />
    );
  }, [isAlarms]);

  useEffect(() => {
    dispatch(setAssetsIds(selectedAsset));
    dispatch(setComponentIds(selectedComponent));
  }, [selectedComponent, selectedAsset]);

  useEffect(() => {
    // Reset Selection
    if (isSensorData) setActiveSensorsDataType(SENSORS_DATA_TYPE.PI);
  }, [isSensorData]);

  return (
    <Layout>
      <div className="flex flex-col md:px-8 w-full h-full gap-4 px-2 py-0 max-h-screen">
        <BaseTabs
          tabs={assetStudioTabs}
          paramAlias={FILTER_PARAMS.ASSET_STUDIO}
          hideTabTriggers={false}
          defaultValue={assetStudio || ASSET_STUDIO.DIAGNOSTICS}
          onClick={handleTabClick}
          renderFixedElement={() => <AssetStudio />}
          renderRightPane={() => {
            return (
              <div className="flex items-center min-w-full justify-between pl-6 gap-6">
                {(isMobileAsset ? !isMaintenance && !isSensorData && !isAlarms : !isMaintenance && !isAlarms) && (
                  <div
                    data-testid="viewing_indicator"
                    className={cn(
                      'w-fit whitespace-nowrap px-4 py-2 rounded-lg font-semibold text-sm ',
                      `${currentDiagnosticHealthStatus}`
                    )}
                  >
                    {`Viewing Diagnostics Data for: ${format(
                      new Date(selectedDiagnostic?.[0] ? selectedDiagnostic?.[0] : new Date()),
                      REGULAR_DATE_FORMAT
                    )}`}
                  </div>
                )}
                {/* TODO: replace all these conditions with proper config */}
                <div className="flex items-center gap-4 justify-end w-full">
                  {(assetStudio === ASSET_STUDIO.ALARMS ||
                    activeSensorsDataType === SENSORS_DATA_TYPE.PI ||
                    activeSensorsDataType === SENSORS_DATA_TYPE.FFT ||
                    activeSensorsDataType === SENSORS_DATA_TYPE.OilReports) && (
                    //@TODO: move to a stand alone component
                    <div className="justify-end flex items-center gap-4 w-full">
                      {isAlarms && <AlarmsActions />}
                      {isSensorData && (
                        <BaseButton
                          size={'sm'}
                          variant="outline"
                          onClick={() => {
                            openDrawer();
                          }}
                          className="h-10"
                          disabled={isViewFaultsDisabled}
                        >
                          {isViewFaultsDisabled ? 'No Faults Available' : 'View Faults'}
                        </BaseButton>
                      )}
                      {((isSensorData &&
                        activeSensorsDataType !== SENSORS_DATA_TYPE.OilReports &&
                        activeSensorsDataType !== SENSORS_DATA_TYPE.FFT) ||
                        isDiagnostics ||
                        isAlarms ||
                        isMaintenance ||
                        isVisualAI) &&
                        MemoizedDatePicker}
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
    </Layout>
  );
};
